// src/screens/CreateTournament.tsx
import {  useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Save, Globe, Lock } from "lucide-react";
import i18n from "../i18n";

/* ===== i18n mini-hook (même pattern que CreateMatch) ===== */


/** Helper: tente createTournament:* puis createtournament:* puis defaultValue */
function makeNamespaceT(nsPreferred = "createTournament", nsFallback = "createtournament") {
  return (relKey: string, defaultValue: string, options?: Record<string, unknown>) => {
    const k1 = `${nsPreferred}:${relKey}`;
    const k2 = `${nsFallback}:${relKey}`;
    // i18next.exists est dispo, mais on force String() quoi qu'il arrive.
    if ((i18n as any).exists?.(k1)) return String((i18n.t as any)(k1, options));
    if ((i18n as any).exists?.(k2)) return String((i18n.t as any)(k2, options));
    // log utile en dev
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[i18n fallback] missing ${k1} / ${k2} → use default`);
      // eslint-disable-next-line no-console
      console.log("bundles:", {
        hasPreferred: (i18n as any).hasResourceBundle?.(i18n.language, nsPreferred),
        hasPreferredFR: (i18n as any).hasResourceBundle?.("fr", nsPreferred),
        hasFallback: (i18n as any).hasResourceBundle?.(i18n.language, nsFallback),
      });
    }
    return defaultValue;
  };
}

export default function CreateTournament() {
  const nav = useNavigate();
  const tt = makeNamespaceT(); // bind sur createTournament / createtournament

  const [form, setForm] = useState({
    name: "",
    location: "",
    start_date: "",
    end_date: "",
    is_public: true,
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) {
      setLoading(false);
      alert(tt("alerts.loginRequired", "Veuillez vous connecter."));
      return;
    }

    const { data, error } = await supabase
      .from("tournaments")
      .insert({
        user_id: user.id,
        name: form.name.trim(),
        location: form.location.trim() || null,
        start_date: form.start_date || null, // YYYY-MM-DD
        end_date: form.end_date || null,     // YYYY-MM-DD
        is_public: form.is_public,
      })
      .select("id")
      .maybeSingle();

    setLoading(false);
    if (error || !data) {
      const message = error?.message ?? "création impossible";
      alert(tt("alerts.createFailed", "Erreur: {{message}}", { message }));
      return;
    }

    nav(`/tournaments/${data.id}/admin`);
  };

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-bold mb-4">
        {tt("title", "Créer un tournoi")}
      </h1>

      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">
            {tt("form.nameLabel", "Nom du tournoi")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={tt("form.namePlaceholder", "Tournoi U18 – Automne")}
            required
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            {tt("form.locationLabel", "Lieu")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={tt("form.locationPlaceholder", "Salle Léon Coquard")}
            value={form.location}
            onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {tt("form.startLabel", "Date de début")}
            </label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2"
              value={form.start_date}
              onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {tt("form.endLabel", "Date de fin")}
            </label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2"
              value={form.end_date}
              onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))}
            />
          </div>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_public}
            onChange={(e) => setForm((s) => ({ ...s, is_public: e.target.checked }))}
          />
          <span className="text-sm flex items-center gap-2">
            {form.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {tt("form.publicToggle", "Rendre le tournoi public")}
          </span>
        </label>

        <button
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border disabled:opacity-50"
          disabled={loading}
        >
          <Save className="w-4 h-4" />
          {loading
            ? tt("buttons.creating", "Création...")
            : tt("buttons.create", "Créer le tournoi")}
        </button>
      </form>
    </div>
  );
}
